import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Loader2, ShieldCheck, ShieldAlert, Ban, RefreshCw, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { UserListItem, UserRole } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const roleBadge = (role: UserRole) => {
  const map: Record<UserRole, { bg: string; text: string; label: string; icon: any }> = {
    admin: { bg: "bg-violet-50 border-violet-200", text: "text-violet-700", label: "Admin", icon: ShieldCheck },
    staff: { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", label: "Staff", icon: ShieldAlert },
    user: { bg: "bg-slate-50 border-slate-200", text: "text-slate-700", label: "User", icon: null as any },
  };
  const c = map[role];
  const Icon = c.icon;
  return (
    <span className={`sg-chip ${c.bg} ${c.text} border`}>
      {Icon && <Icon className="h-3 w-3" />} {c.label}
    </span>
  );
};

const AdminUsersPage: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const users = useQuery<UserListItem[]>({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ name: string; email: string; password: string; role: UserRole }>({
    name: "",
    email: "",
    password: "",
    role: "user",
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", form)).data,
    onSuccess: () => {
      toast.success("User created. Share the temp password securely.");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "user" });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Failed to create user"),
  });

  const changeRole = useMutation({
    mutationFn: async (v: { user_id: string; role: UserRole }) =>
      (await api.put(`/users/${v.user_id}/role`, { role: v.role })).data,
    onSuccess: () => {
      toast.success("Role updated");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Failed to update role"),
  });

  const toggleActive = useMutation({
    mutationFn: async (v: { user_id: string; active: boolean }) =>
      (await api.put(`/users/${v.user_id}/active`, { active: v.active })).data,
    onSuccess: (_d, v) => {
      toast.success(v.active ? "User reactivated" : "User deactivated");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Failed to update user"),
  });

  return (
    <DashboardLayout
      title="Team & Roles"
      subtitle="Manage who can access your SociBaba workspace and at what level."
      seoPath="/admin/users"
      seoDescription="Manage workspace members, roles, and access."
    >
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="sg-card px-3 py-2 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-violet-600" />
            <span><b>Admin</b> — full access, credentials, refunds, user management</span>
          </div>
          <div className="sg-card px-3 py-2 flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5 text-blue-600" />
            <span><b>Staff</b> — read-only Admin Health + API errors (no secrets)</span>
          </div>
          <div className="sg-card px-3 py-2 flex items-center gap-2">
            <span className="h-3.5 w-3.5 rounded-full bg-slate-400 inline-block" />
            <span><b>User</b> — content creator; no admin access</span>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-[color:var(--sg-fg)] text-white hover:bg-black">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite team member</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input data-testid="new-user-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="new-user-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Temporary password</Label>
                <Input data-testid="new-user-password" type="text" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 6 chars — share securely, they can change it later" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as UserRole })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User — content creator</SelectItem>
                    <SelectItem value="staff">Staff — read-only admin</SelectItem>
                    <SelectItem value="admin">Admin — full access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                data-testid="submit-new-user"
                disabled={create.isPending || !form.name || !form.email || form.password.length < 6}
                onClick={() => create.mutate()}
                className="bg-[color:var(--sg-fg)] text-white hover:bg-black"
              >
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Create user
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="sg-card overflow-hidden" data-testid="users-table">
        {users.isLoading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-[color:var(--sg-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--sg-muted-bg)] text-[11px] uppercase tracking-wider text-[color:var(--sg-muted)]">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(users.data || []).map((u) => {
                const isSelf = u.user_id === me?.user_id;
                return (
                  <tr key={u.user_id} className="border-t border-[color:var(--sg-border)]"
                      data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{u.name || u.email.split("@")[0]}</div>
                        {u.is_owner && (
                          <span className="sg-chip bg-amber-50 border-amber-200 text-amber-800 text-[10px]">
                            <Crown className="h-3 w-3" /> Owner
                          </span>
                        )}
                        {isSelf && <span className="text-[10px] text-[color:var(--sg-muted)]">(you)</span>}
                      </div>
                      <div className="text-xs text-[color:var(--sg-muted)]">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">{roleBadge(u.role)}</td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="sg-chip bg-emerald-50 border-emerald-200 text-emerald-700">Active</span>
                      ) : (
                        <span className="sg-chip bg-slate-100 border-slate-200 text-slate-600">Deactivated</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[color:var(--sg-muted)]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Select
                          value={u.role}
                          onValueChange={(v) => changeRole.mutate({ user_id: u.user_id, role: v as UserRole })}
                          disabled={isSelf || !u.active}
                        >
                          <SelectTrigger
                            data-testid={`role-select-${u.email}`}
                            className="h-8 w-[110px] text-xs"
                          ><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.active ? (
                          <Button
                            data-testid={`deactivate-${u.email}`}
                            size="sm" variant="outline"
                            disabled={isSelf}
                            onClick={() => toggleActive.mutate({ user_id: u.user_id, active: false })}
                            className="h-8 text-xs text-red-600 hover:bg-red-50 border-red-200"
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" /> Deactivate
                          </Button>
                        ) : (
                          <Button
                            data-testid={`reactivate-${u.email}`}
                            size="sm" variant="outline"
                            onClick={() => toggleActive.mutate({ user_id: u.user_id, active: true })}
                            className="h-8 text-xs"
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.data && users.data.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[color:var(--sg-muted)]">
                  No team members yet. Click &ldquo;Add user&rdquo; above.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
